import {
  useQuery,
  keepPreviousData,
  type UseQueryResult,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { useDataApiBaseUrl, useDataApiAuthToken } from './DataApiProvider';
import { dataApiFetch, DataApiError, type QueryParams } from './client';

/**
 * Status 0 sentinel: the data API base URL is not configured (VITE_DATA_API_URL
 * unset). Distinct from a network/HTTP failure so consumers can show a clear
 * "not configured" notice instead of a generic error. See `isNotConfigured`.
 */
export const NOT_CONFIGURED_STATUS = 0;

export function isNotConfiguredError(error: unknown): boolean {
  return error instanceof DataApiError && error.status === NOT_CONFIGURED_STATUS;
}

/**
 * Internal factory behind every data hook. Encodes the caching policy so the
 * dashboard stays fresh without hammering the API:
 *
 *  - `live`      — values that advance (overview / series): short staleTime so a
 *                  remount/refocus refetches, but a 5-min poll (30-min bucket
 *                  cadence ⇒ ample) instead of tight intervals.
 *  - `reference` — lists / logs / selectors: 5-min staleTime, NO polling.
 *  - `static`    — effectively-immutable detail (e.g. a closed position): 30-min.
 *
 * The queryKey embeds the full path + every param, so changing the selected
 * safe/vault/position/range/page yields a NEW key → a refetch (never stale data).
 * `keepPreviousData` avoids an empty flash while the new key loads.
 */
export type CacheTier = 'live' | 'reference' | 'static';

interface TierConfig {
  staleTime: number;
  gcTime: number;
  refetchInterval: number | false;
  refetchOnWindowFocus: boolean;
}

const MINUTE = 60_000;

const TIER: Record<CacheTier, TierConfig> = {
  live: { staleTime: MINUTE, gcTime: 10 * MINUTE, refetchInterval: 5 * MINUTE, refetchOnWindowFocus: true },
  reference: { staleTime: 5 * MINUTE, gcTime: 30 * MINUTE, refetchInterval: false, refetchOnWindowFocus: false },
  static: { staleTime: 30 * MINUTE, gcTime: 60 * MINUTE, refetchInterval: false, refetchOnWindowFocus: false },
};

export type DataQueryOptions<T> = Omit<UseQueryOptions<T, Error, T>, 'queryKey' | 'queryFn'>;

export function useDataQuery<T>(args: {
  path: string;
  params?: QueryParams;
  tier?: CacheTier;
  /** Gate the request (e.g. until a safe/vault is selected). Default true. */
  enabled?: boolean;
  /**
   * Send the DataApiProvider's bearer token (per-wallet analytics endpoints).
   * When true, the request is additionally gated on a token being present, so it
   * never fires unauthenticated (which would 401).
   */
  authenticated?: boolean;
  options?: DataQueryOptions<T>;
}): UseQueryResult<T, Error> {
  const { path, params, tier = 'reference', enabled = true, authenticated = false, options } = args;
  const baseUrl = useDataApiBaseUrl();
  const authToken = useDataApiAuthToken();
  const configured = baseUrl.trim().length > 0;
  const t = TIER[tier];

  // Hooks must run unconditionally — always call useQuery (kept disabled when the
  // API isn't configured so it never fires a request against an empty base URL).
  const result = useQuery<T, Error, T>({
    // `authenticated` (not the token itself) is in the key: the token refreshes
    // hourly and we don't want that to invalidate every cached query; the queryFn
    // reads the current token at fetch time.
    queryKey: ['thema-data-api', path, params ?? null, authenticated],
    queryFn: ({ signal }) =>
      dataApiFetch<T>(baseUrl, path, params, signal, authenticated ? authToken : undefined),
    enabled: enabled && configured && (!authenticated || !!authToken),
    placeholderData: keepPreviousData,
    staleTime: t.staleTime,
    gcTime: t.gcTime,
    refetchInterval: t.refetchInterval,
    refetchOnWindowFocus: t.refetchOnWindowFocus,
    retry: 1,
    ...options,
  });

  // When VITE_DATA_API_URL is unset the query stays disabled, which React-Query
  // reports as `isPending` forever (an infinite skeleton). Surface it instead as
  // a terminal error so the consuming component renders its error/notice state
  // and the dashboard never goes blank or hangs on a spinner.
  if (!configured) {
    return {
      ...result,
      data: undefined,
      error: new DataApiError(
        NOT_CONFIGURED_STATUS,
        'Live data is unavailable — the data API is not configured.',
      ),
      status: 'error',
      isError: true,
      isPending: false,
      isLoading: false,
      isLoadingError: true,
      isRefetchError: false,
      isSuccess: false,
      isPlaceholderData: false,
      fetchStatus: 'idle',
    } as UseQueryResult<T, Error>;
  }

  return result;
}
